import React, { useState } from 'react';
import axios from 'axios';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  Button,
  FileInput,
  Alert,
  Badge,
  Table,
  Divider
} from '@mantine/core';
import { IconUpload, IconDownload, IconAlertCircle, IconCheck } from '@tabler/icons-react';

const AdminProductImportPage = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const downloadTemplate = async () => {
    const response = await axios.get('/admin/products/import-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'giftpersona-product-import-template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/admin/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Container size="md" py="xl">
      <Title order={1} size="h2" mb={4}>Import products</Title>
      <Text c="dimmed" size="sm" mb="xl">
        Bulk-add products to the recommendation catalog from a CSV — useful for hand-curated
        picks, affiliate exports, or anything that isn't covered by a live marketplace search.
      </Text>

      <Card p="lg" radius="lg" withBorder mb="xl">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text fw={600} size="sm">Not sure of the format?</Text>
              <Text size="xs" c="dimmed">
                Columns: name (required), brand, category, price, model_number, url, image_url, description, rating, review_count
              </Text>
            </div>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconDownload size={14} />}
              onClick={downloadTemplate}
            >
              Download template
            </Button>
          </Group>

          <Divider />

          <FileInput
            label="CSV file"
            placeholder="Choose a .csv file"
            accept=".csv,text/csv"
            value={file}
            onChange={setFile}
            leftSection={<IconUpload size={16} />}
            clearable
          />

          <Text size="xs" c="dimmed">
            Re-uploading a CSV updates existing rows that share the same model_number instead of
            duplicating them. Rows with no model_number are always added as new.
          </Text>

          <Button onClick={handleImport} loading={importing} disabled={!file} w="fit-content">
            Upload &amp; import
          </Button>
        </Stack>
      </Card>

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="lg">
          {error}
        </Alert>
      )}

      {result && (
        <Card p="lg" radius="lg" withBorder>
          <Group mb="md" gap="xs">
            <IconCheck size={18} color="var(--mantine-color-teal-6)" />
            <Text fw={600} size="sm">Import finished</Text>
          </Group>
          <Group gap="xs" mb="md">
            <Badge color="teal" variant="light">{result.summary.inserted} added</Badge>
            <Badge color="blue" variant="light">{result.summary.updated} updated</Badge>
            {result.summary.failed > 0 && (
              <Badge color="red" variant="light">{result.summary.failed} failed</Badge>
            )}
          </Group>

          {result.failures.length > 0 && (
            <>
              <Text fw={600} size="xs" mb="xs">Rows that failed:</Text>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Row</Table.Th>
                    <Table.Th>Error</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {result.failures.map(f => (
                    <Table.Tr key={f.row}>
                      <Table.Td>{f.row}</Table.Td>
                      <Table.Td>{f.error}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </>
          )}
        </Card>
      )}
    </Container>
  );
};

export default AdminProductImportPage;
